import { ListingType, PropertyType } from '@zg-rentals/ts-rental-manager-api-client';

import PropertyConstants from '../constants/PropertyConstants';

import numberUtils from './numberUtils';
import stringHelper from './stringHelper';

import type {
  GetUserPropertyDetailsAmenitiesGroupWebDto,
  GetUserPropertyDetailsFloorPlanWebDto,
  GetUserPropertyDetailsListingTypeWebDto,
  GetUserPropertyDetailsPetsAllowedWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

const propertyUtils = {
  isRoomForRent({ listing }: { listing?: { listingType?: GetUserPropertyDetailsListingTypeWebDto } }) {
    return listing?.listingType?.code === PropertyConstants.LISTING_CODES_BY_TYPE[PropertyConstants.LISTING_TYPES.ROOM];
  },
  formatFloorPlanDisplayPrice(lowPrice: number, highPrice: number) {
    const formattedLowPrice = numberUtils.asCurrencyWithDefault(lowPrice, '', '$0,0');
    const formattedHighPrice = numberUtils.asCurrencyWithDefault(highPrice, '', '$0,0');

    switch (true) {
      case !lowPrice && !highPrice:
        return '$ -';
      case lowPrice === highPrice:
        return `${formattedLowPrice}`;
      case Boolean(lowPrice && highPrice):
        return `${formattedLowPrice}-${formattedHighPrice}`;
      default:
        return `${formattedLowPrice || formattedHighPrice}`;
    }
  },
  getActiveFloorPlanOrUnitCount(floorPlans: Array<GetUserPropertyDetailsFloorPlanWebDto>) {
    let activeCount = 0;

    floorPlans.forEach((floorPlan) => {
      if (floorPlan.units) {
        floorPlan.units.forEach((unit) => {
          if (unit.active) {
            activeCount += 1;
          }
        });
      }

      if (floorPlan?.units?.length === 0 && floorPlan.active) {
        activeCount += 1;
      }
    });

    return activeCount;
  },
  getFloorPlanDescription({
    numBathrooms,
    numBedrooms,
    lowPrice,
    highPrice,
    squareFeet,
    units,
  }: Partial<GetUserPropertyDetailsFloorPlanWebDto>) {
    const activeUnits = units
      ? units.reduce((accumulator: Array<{ lowPrice: number; highPrice: number }>, unit) => {
          if (unit.active) {
            accumulator.push({ lowPrice: unit.lowPrice, highPrice: unit.highPrice });
          }
          return accumulator;
        }, [])
      : [];

    const numActive = activeUnits.length;
    const lowActivePrice = numActive && Math.min(...activeUnits.map((unit) => unit.lowPrice));
    const highActivePrice = numActive && Math.max(...activeUnits.map((unit) => unit.highPrice));

    const displayPrice = this.formatFloorPlanDisplayPrice(
      lowActivePrice || Number(lowPrice),
      highActivePrice || Number(highPrice),
    );
    const displayTextForBaths = !numBathrooms ? '- baths' : `${stringHelper.pluralize(numBathrooms, 'bath', 'baths')}`;
    const displaySqft = squareFeet ? ` | ${squareFeet} sqft` : '';
    const displayBeds = numBedrooms || numBedrooms === 0 ? Number(numBedrooms) : '-';
    const displayTextForBeds = displayBeds === 0 ? 'studio' : `${stringHelper.pluralize(displayBeds, 'bed', 'beds')}`;

    return {
      displayPrice,
      fullDisplayText: `${displayPrice} | ${displayTextForBeds} | ${displayTextForBaths}${displaySqft}`,
    };
  },
  getIsPetsAllowedEmpty(petsAllowed?: Partial<GetUserPropertyDetailsPetsAllowedWebDto>) {
    const { negotiable: _, ...petsAllowedDtos } = petsAllowed ?? {};
    return !Object.values(petsAllowedDtos).some((petsAllowedValue) =>
      typeof petsAllowedValue === 'boolean' ? petsAllowedValue !== false : petsAllowedValue?.allowed !== null,
    );
  },
  checkIsAmenityFieldFulfilled: ({
    amenityField,
  }: {
    amenityField?: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>;
  }) => {
    const requiredArray = amenityField?.filter(({ isRequired }) => isRequired);

    if (!requiredArray?.length) {
      return true;
    }

    return Boolean(
      requiredArray?.some(({ values }) => {
        let fulfilled = false;

        values.forEach(({ isSelected }) => {
          fulfilled = fulfilled || isSelected;
        });

        return fulfilled;
      }),
    );
  },

  formatUnitInfo(bedrooms?: number | null, bathrooms?: number | null, squareFeet?: number | null) {
    if (bedrooms == null && bathrooms == null && squareFeet == null) return 'Unknown size';
    const bedLabel = bedrooms === 0 ? 'Studio' : `${bedrooms ?? '—'} bed`;
    return `${bedLabel} • ${bathrooms ?? '—'} bath • ${squareFeet ?? '—'} sqft`;
  },

  getUnitTypeLabel({ propertyType, listingType }: { propertyType?: PropertyType; listingType?: ListingType }) {
    const { ROOM_FOR_RENT_LABEL, SHORT_PROPERTY_TYPES_BY_ENUM } = PropertyConstants;

    if (listingType === ListingType.ROOM) {
      return ROOM_FOR_RENT_LABEL;
    }

    return propertyType === undefined ? undefined : SHORT_PROPERTY_TYPES_BY_ENUM[propertyType];
  },
};

export default propertyUtils;
